'use client'

import React, { useState } from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

interface BulkStatusManagementDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedAnmeldungen: string[]
  anmeldungen: PopulatedAnmeldung[]
  onBulkStatusUpdate: (anmeldungIds: string[], newStatus: string) => Promise<void>
}

const statusOptions = [
  { value: 'submitted', label: 'Eingereicht', color: 'bg-amber-100 text-amber-800' },
  { value: 'confirmed', label: 'Bestätigt', color: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Abgelehnt', color: 'bg-red-100 text-red-800' },
  { value: 'waitlist', label: 'Warteliste', color: 'bg-gray-100 text-gray-700' },
]

export const BulkStatusManagementDialog: React.FC<BulkStatusManagementDialogProps> = ({
  isOpen,
  onClose,
  selectedAnmeldungen,
  anmeldungen,
  onBulkStatusUpdate,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  const selectedAnmeldungenData = anmeldungen.filter((a) => selectedAnmeldungen.includes(a.id))

  const handleSave = async () => {
    if (!selectedStatus) return

    setIsLoading(true)
    try {
      await onBulkStatusUpdate(selectedAnmeldungen, selectedStatus)
      onClose()
    } catch (error) {
      console.error('Error updating bulk status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative mx-4 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-lg font-semibold">
            Status für {selectedAnmeldungen.length} Anmeldungen ändern
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Selected Participants List */}
          <div className="mb-6 rounded-lg bg-gray-50 p-4">
            <h3 className="mb-4 font-medium">Ausgewählte Teilnehmer</h3>
            <div className="max-h-40 space-y-2 overflow-y-auto">
              {selectedAnmeldungenData.map((anmeldung) => (
                <div key={anmeldung.id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {anmeldung.teilnehmer.firstName} {anmeldung.teilnehmer.lastName}
                  </span>
                  <span className="text-gray-500">{anmeldung.camp.title}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {/* Status Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Neuer Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Status auswählen...</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !selectedStatus}
            className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Speichern...' : 'Status ändern'}
          </button>
        </div>
      </div>
    </div>
  )
}
